import { Box, Button, Grid, GridColumn } from "@modernatx-internal/component-library";
import React from "react";

import { ContentComponentProps, IconCardContent, IconCardGroupContent } from "@/content-V2/types";
import { useExperience } from "@/context/ExperienceContext";

const getTotalWidth = (node: Element | null): number => {
  if (!node) {
    return 0;
  }

  return (
    parseInt(window.getComputedStyle(node).width, 10) +
    parseInt(window.getComputedStyle(node).marginLeft, 10) +
    parseInt(window.getComputedStyle(node).marginRight, 10)
  );
};

export const IconCard: React.FC<ContentComponentProps<IconCardContent>> = ({
  children,
  size = [3, 3, 3],
  sx
}) => {
  return (
    <GridColumn data-icon-card-column={true} size={size}>
      <Box
        sx={{
          alignItems: "center",
          backgroundColor: "surface-neutral",
          border: "1px solid",
          borderColor: "stroke-neutral",
          borderRadius: "radius-3",
          boxShadow: "shadow-300",
          display: "flex",
          flexDirection: "column",
          gap: 5,
          height: "100%",
          justifyContent: "start",
          minHeight: 56,
          p: 8,
          textAlign: "center",
          ...sx
        }}
      >
        {children}
      </Box>
    </GridColumn>
  );
};

export const IconCardGroup: React.FC<ContentComponentProps<IconCardGroupContent>> = ({
  children
}) => {
  const { expandedMode } = useExperience();
  const cardChildren = React.Children.toArray(children) as React.ReactElement[];
  const [scrollState, setScrollState] = React.useState({
    nextDisabled: false,
    prevDisabled: false
  });
  const scrollElement = React.useRef<HTMLDivElement | null>(null);
  const firstGridColumnElement = React.useRef<Element | null>(null);

  const handleClickNext = React.useCallback(() => {
    if (scrollElement.current) {
      const { clientWidth, scrollLeft, scrollWidth } = scrollElement.current;

      if (clientWidth >= scrollWidth / 2) {
        scrollElement.current.scrollTo({
          left: scrollLeft + clientWidth,
          behavior: "smooth"
        });
      } else {
        const columnWidth = getTotalWidth(firstGridColumnElement.current);
        scrollElement.current.scrollTo({
          left: scrollLeft + columnWidth,
          behavior: "smooth"
        });
      }
    }
  }, []);

  const handleClickPrev = React.useCallback(() => {
    if (scrollElement.current) {
      const { clientWidth, scrollLeft, scrollWidth } = scrollElement.current;

      if (clientWidth >= scrollWidth / 2) {
        scrollElement.current.scrollTo({
          left: scrollLeft - clientWidth,
          behavior: "smooth"
        });
      } else {
        const columnWidth = getTotalWidth(firstGridColumnElement.current);
        scrollElement.current.scrollTo({
          left: scrollLeft - columnWidth,
          behavior: "smooth"
        });
      }
    }
  }, []);

  const handleScroll = React.useCallback(() => {
    if (scrollElement.current) {
      const { clientWidth, scrollLeft, scrollWidth } = scrollElement.current;
      const scrollLeftCeil = Math.ceil(scrollLeft);
      const scrollMax = Math.floor(scrollWidth - clientWidth);
      const isScrollable = scrollWidth > clientWidth;
      const nextDisabled = !isScrollable || scrollLeftCeil >= scrollMax;
      const prevDisabled = !isScrollable || scrollLeftCeil <= 0;

      setScrollState((previous) =>
        previous.nextDisabled === nextDisabled && previous.prevDisabled === prevDisabled
          ? previous
          : { nextDisabled, prevDisabled }
      );
    }
  }, []);

  React.useEffect(() => {
    firstGridColumnElement.current =
      scrollElement.current?.querySelector("[data-icon-card-column]") ?? null;
    handleScroll();
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("resize", handleScroll);
    };
  }, [handleScroll]);

  if (!cardChildren.length) {
    return null;
  }

  return (
    <>
      <Box
        ref={scrollElement}
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          py: 8,
          scrollbarWidth: "none",
          width: "100%",
          "::-webkit-scrollbar": {
            display: "none"
          }
        }}
        tabIndex={0}
        onScroll={handleScroll}
      >
        <Grid gap={expandedMode ? 6 : 0} wrap={expandedMode ? true : false}>
          {cardChildren}
        </Grid>
      </Box>
      <Grid>
        <GridColumn
          sx={{
            display: cardChildren.length <= 4 ? ["flex", null, "none"] : "flex",
            flexDirection: "row",
            gap: 2,
            mt: -3
          }}
        >
          <Button
            aria-label="Go to previous card"
            disabled={scrollState.prevDisabled}
            icon="caret-left"
            variant="outline"
            onClick={handleClickPrev}
          />
          <Button
            aria-label="Go to next card"
            disabled={scrollState.nextDisabled}
            icon="caret-right"
            variant="outline"
            onClick={handleClickNext}
          />
        </GridColumn>
      </Grid>
    </>
  );
};
